import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon';
import { postJson } from '../global/func';

/**
 * What the customer calls this box.
 *
 * A Prayer Box has always been a serial number -- 00000000b8136ab9 -- which is
 * the board's identity and no use to the person who owns it. Someone with two
 * has to remember which sixteen hex digits is the living room.
 *
 * The serial is shown underneath rather than replaced. It is the unit's real
 * identity: enrolment, the hardware check and every support conversation turn
 * on it, so it has to stay readable off the screen when somebody rings up. For
 * an administrator the owner's email sits beside it, because on the fleet list
 * "Living room" without an owner is worse than a serial number.
 */
const DeviceName = ({ device, user, onRenamed }) => {
    const [name, setName] = useState(device?.name || '');
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');

    // Kept in step with the device being looked at, or switching boxes would
    // leave the previous one's name in the field.
    useEffect(() => {
        setName(device?.name || '');
        setSaved(false);
        setError('');
    }, [device?.id, device?.name]);

    const unchanged = (name.trim() || '') === (device?.name || '');

    const save = async () => {
        if (busy || unchanged) return;
        setBusy(true);
        setError('');
        setSaved(false);

        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/rename/');
        const result = await postJson(url, {
            device_id: device.id,
            name: name.trim(),
            USER_ID: user.id,
            AUTH_KEY: user.auth_key,
        });

        setBusy(false);

        if (!result?.ok) {
            setError(result?.data?.error
                || 'That name could not be saved. Check your connection and try again.');
            return;
        }

        setSaved(true);
        if (onRenamed) onRenamed();
    };

    return (
        <>
            <div className="field">
                <label className="field__label" htmlFor="device-name">Name</label>
                <input
                    id="device-name"
                    type="text"
                    value={name}
                    maxLength={50}
                    disabled={busy}
                    placeholder="Prayer Box"
                    onChange={(e) => { setName(e.currentTarget.value); setSaved(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            </div>

            <div className="name-row">
                <span className="muted">
                    {device?.serial_no}
                    {device?.owner_email && <> &middot; {device.owner_email}</>}
                </span>
                <button className="btn btn--sm" onClick={save} disabled={busy || unchanged}>
                    {busy ? <span className="spinner" />
                        : saved ? <Icon name="check" size={14} />
                            : null}
                    {busy ? 'Saving…' : saved ? 'Saved' : 'Save name'}
                </button>
            </div>

            {error && <div className="notice notice--warn" role="alert">{error}</div>}
        </>
    );
};

DeviceName.propTypes = {
    device:    PropTypes.object,
    user:      PropTypes.object.isRequired,
    onRenamed: PropTypes.func,
};

export default DeviceName;
